import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';
import { cn } from '@lib/utils';
import { useGlassStyles } from '@hooks/useGlassStyles';
// import { getContrastColor } from '@styles/glass-effects';

interface DialogContentProps extends React.ComponentProps<typeof DialogPrimitive.Content> {
  showCloseButton?: boolean;
  dominantColor?: string;
  primaryButton?: {
    label: string;
    onClick: () => void;
  };
  secondaryButton?: {
    label: string;
    onClick: () => void;
  };
}

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[100] bg-black/30',
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  dominantColor = 'rgb(203, 203, 203)',
  primaryButton,
  secondaryButton,
  ...props
}: DialogContentProps) {
  const { glassStyle, waterDropStyle } = useGlassStyles(dominantColor);
  // const textColorClass = getContrastColor(dominantColor);

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          '!fixed left-1/2 top-1/2 z-[100]',
          'w-[90%] max-w-2xl max-h-[90vh]',
          '-translate-x-1/2 -translate-y-1/2',
          'rounded-xl p-6',
          'overflow-y-auto',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
          className,
        )}
        style={{
          ...glassStyle,
          ...waterDropStyle,
          backdropFilter: 'blur(30px)',
          WebkitBackdropFilter: 'blur(30px)',
        }}
        {...props}
      >
        <div className="flex flex-col h-full">
          <div className={cn('text-content text-white', 'drop-shadow-md')}>{children}</div>

          {/* Action Buttons */}
          {(primaryButton || secondaryButton) && (
            <div className="flex justify-end gap-3 mt-6">
              {secondaryButton && (
                <button
                  onClick={secondaryButton.onClick}
                  className={cn(
                    'px-4 py-2 rounded-lg font-medium',
                    'border-2 border-current',
                    'transition-all duration-200',
                    'hover:bg-white/20 hover:scale-105',
                    'drop-shadow-md',
                    'text-white border-white/80',
                  )}
                >
                  {secondaryButton.label}
                </button>
              )}
              {primaryButton && (
                <button
                  onClick={primaryButton.onClick}
                  className={cn(
                    'px-4 py-2 rounded-lg font-medium',
                    'transition-all duration-200',
                    'hover:scale-105 drop-shadow-lg',
                    'bg-white text-black hover:bg-white/95 shadow-lg',
                  )}
                >
                  {primaryButton.label}
                </button>
              )}
            </div>
          )}

          {/* Close Button */}
          {showCloseButton && (
            <DialogPrimitive.Close
              data-slot="dialog-close"
              className={cn(
                'absolute right-4 top-4',
                'p-2 rounded-full',
                'opacity-70 hover:opacity-100',
                'transition-opacity duration-200',
                'text-white',
              )}
            >
              <XIcon className="w-5 h-5" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </div>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('text-xl leading-tight font-bold drop-shadow-sm text-white', className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-base opacity-90 drop-shadow-sm font-medium text-white', className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
